import { useState, useMemo } from 'react';
import { Row, Col, Card, Form, Tab, Nav, Spinner } from 'react-bootstrap';
import ReactEChartsCore from '../../components/charts/EChart';
import * as echarts from 'echarts/core';
import { TooltipComponent, LegendComponent, GridComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../providers/AppProvider.js';
import {
  useOperatorSummary,
  useOperatorMatrix,
  useMaterialMatrix,
  useOperatorTrend
} from '../../features/production-metrics/hooks/useProductionMetrics.js';
import {
  useIntervalStats,
  useDowntimeMatrix
} from '../../features/machine-intervals/hooks/useMachineIntervals.js';
import OperatorMatrixTable from '../../components/operators/OperatorMatrixTable.js';
import DowntimeMatrixTable from '../../components/operators/DowntimeMatrixTable.js';
import MaterialMatrixTable from '../../components/operators/MaterialMatrixTable.js';
import OperatorTrendChart from '../../components/operators/OperatorTrendChart.js';
import DrillTokensModal from '../../components/operators/DrillTokensModal.js';
import OperatorScopeBar, {
  PHASES,
  TAB_FILTERS,
  thisMonth
} from '../../components/operators/OperatorScopeBar.js';
import OperatorStrip from '../../components/operators/OperatorStrip.js';
import dayjs from 'dayjs';

echarts.use([TooltipComponent, LegendComponent, GridComponent, BarChart, CanvasRenderer]);

const COLORS = [
  '#3874ff', '#0fc6c2', '#e5780b', '#8b5cf6', '#f43f5e',
  '#10b981', '#f59e0b', '#6366f1', '#ec4899', '#14b8a6',
  '#ef4444', '#84cc16', '#06b6d4', '#a855f7', '#f97316', '#22d3ee'
];

const fmtMin = (sec) => {
  if (sec == null) return '-';
  const m = Math.round(sec / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

const fmtKm = (m) => {
  if (m == null) return '-';
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
};

const fmtKg = (v) => v == null ? '-' : `${Number(v).toFixed(1)} kg`;

const fmtSpeed = (v) => v == null ? '-' : `${Number(v).toFixed(1)} m/min`;

// ── KPI Card ──
const KpiCard = ({ title, value, subtitle, color = 'primary' }) => (
  <Card className="h-100">
    <Card.Body className="py-3">
      <p className="text-700 fs-10 mb-1">{title}</p>
      <h3 className={`mb-0 text-${color}`}>{value}</h3>
      {subtitle && <p className="text-700 fs-10 mb-0 mt-1">{subtitle}</p>}
    </Card.Body>
  </Card>
);

// ── Chart wrapper ──
const ChartCard = ({ title, children, height = '350px' }) => (
  <Card className="h-100">
    <Card.Header className="py-2">
      <h6 className="mb-0">{title}</h6>
    </Card.Header>
    <Card.Body className="p-0" style={{ minHeight: height }}>
      {children}
    </Card.Body>
  </Card>
);

const LoadingState = () => (
  <div className="d-flex align-items-center justify-content-center py-6">
    <Spinner animation="border" variant="primary" size="sm" className="me-2" />
    <span className="text-700">Loading data...</span>
  </div>
);

const EmptyState = ({ message = 'No data available for selected filters' }) => (
  <div className="text-center py-6 text-700">{message}</div>
);

// ════════════════════════════════════════════════════════════════════
// TAB 1: OVERVIEW
// ════════════════════════════════════════════════════════════════════
const OverviewTab = ({ filters }) => {
  const { getThemeColor } = useAppContext();
  const { data, isLoading } = useOperatorSummary(filters);
  const operators = data?.data || [];

  if (isLoading) return <LoadingState />;
  if (!operators.length) return <EmptyState />;

  const totalMeters = operators.reduce((s, o) => s + (o.totalProducedLength || 0), 0);
  const totalTokens = operators.reduce((s, o) => s + (o.totalTokens || 0), 0);
  const totalRunningSec = operators.reduce((s, o) => s + (o.totalRunningSec || 0), 0);
  const totalIdleSec = operators.reduce((s, o) => s + (o.totalIdleSec || 0), 0);
  // Metres over running time. Averaging the per-operator averages would weight a
  // 200 m drum the same as a 10 km one.
  const overallAvgSpeed = totalRunningSec > 0 ? totalMeters / (totalRunningSec / 60) : 0;

  const byMeters = [...operators].sort((a, b) => b.totalProducedLength - a.totalProducedLength);
  const bySpeed = [...operators].sort((a, b) => (b.avgSpeed || 0) - (a.avgSpeed || 0));
  const byTime = [...operators].sort(
    (a, b) => (b.totalRunningSec || 0) + (b.totalIdleSec || 0) - ((a.totalRunningSec || 0) + (a.totalIdleSec || 0))
  );

  // Running vs idle at the machine. Without this, an operator who spent a shift
  // on a line that was down looks identical to one who simply produced little.
  const timeOption = {
    tooltip: {
      trigger: 'axis', axisPointer: { type: 'shadow' },
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      formatter: (ps) => {
        const run = ps.find(p => p.seriesName === 'Running')?.value || 0;
        const idle = ps.find(p => p.seriesName === 'Idle')?.value || 0;
        const total = run + idle;
        const pct = total > 0 ? Math.round((run / total) * 100) : 0;
        return `<b>${ps[0]?.name}</b><br/>Running: ${fmtMin(run)}<br/>Idle: ${fmtMin(idle)}<br/>Utilisation: ${pct}%`;
      }
    },
    legend: { data: ['Running', 'Idle'], textStyle: { color: getThemeColor('gray-700') }, top: 0, right: 10 },
    grid: { left: 140, right: 40, top: 30, bottom: 10, containLabel: false },
    xAxis: {
      type: 'value', name: 'sec', nameTextStyle: { color: getThemeColor('gray-600') },
      axisLabel: { color: getThemeColor('gray-600'), fontSize: 11, formatter: (v) => fmtMin(v) },
      splitLine: { lineStyle: { color: getThemeColor('gray-200') } }
    },
    yAxis: {
      type: 'category', data: byTime.map(o => o._id),
      axisLabel: { color: getThemeColor('gray-800'), fontSize: 12 }, axisTick: false,
      axisLine: { lineStyle: { color: getThemeColor('gray-300') } }
    },
    series: [
      {
        name: 'Running', type: 'bar', stack: 'time', barWidth: 18,
        data: byTime.map(o => Math.round(o.totalRunningSec || 0)),
        itemStyle: { color: getThemeColor('success') }
      },
      {
        name: 'Idle', type: 'bar', stack: 'time', barWidth: 18,
        data: byTime.map(o => Math.round(o.totalIdleSec || 0)),
        itemStyle: { color: getThemeColor('gray-400'), borderRadius: [0, 3, 3, 0] }
      }
    ]
  };

  const metersOption = {
    tooltip: {
      trigger: 'axis', axisPointer: { type: 'shadow' },
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      formatter: (p) => `<b>${p[0]?.name}</b><br/>${fmtKm(p[0]?.value)}`
    },
    grid: { left: 140, right: 30, top: 10, bottom: 10, containLabel: false },
    xAxis: { type: 'value', axisLabel: { color: getThemeColor('gray-600'), fontSize: 11 }, splitLine: { lineStyle: { color: getThemeColor('gray-200') } } },
    yAxis: { type: 'category', data: byMeters.map(o => o._id), axisLabel: { color: getThemeColor('gray-800'), fontSize: 12 }, axisTick: false, axisLine: { lineStyle: { color: getThemeColor('gray-300') } } },
    series: [{
      type: 'bar', data: byMeters.map((o, i) => ({ value: Math.round(o.totalProducedLength), itemStyle: { color: COLORS[i % COLORS.length], borderRadius: [0, 3, 3, 0] } })),
      barWidth: 18, label: { show: true, position: 'right', fontSize: 11, color: getThemeColor('gray-700'), formatter: (p) => fmtKm(p.value) }
    }]
  };

  const speedOption = {
    tooltip: {
      trigger: 'axis', axisPointer: { type: 'shadow' },
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      formatter: (p) => `<b>${p[0]?.name}</b><br/>${fmtSpeed(p[0]?.value)}`
    },
    grid: { left: 140, right: 30, top: 10, bottom: 10, containLabel: false },
    xAxis: { type: 'value', name: 'm/min', nameTextStyle: { color: getThemeColor('gray-600') }, axisLabel: { color: getThemeColor('gray-600'), fontSize: 11 }, splitLine: { lineStyle: { color: getThemeColor('gray-200') } } },
    yAxis: { type: 'category', data: bySpeed.map(o => o._id), axisLabel: { color: getThemeColor('gray-800'), fontSize: 12 }, axisTick: false, axisLine: { lineStyle: { color: getThemeColor('gray-300') } } },
    series: [{
      type: 'bar', data: bySpeed.map((o, i) => ({ value: Number((o.avgSpeed || 0).toFixed(1)), itemStyle: { color: COLORS[i % COLORS.length], borderRadius: [0, 3, 3, 0] } })),
      barWidth: 18, label: { show: true, position: 'right', fontSize: 11, color: getThemeColor('gray-700'), formatter: (p) => `${p.value} m/min` }
    }]
  };

  const chartH = Math.max(250, operators.length * 40 + 40);

  return (
    <>
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}><KpiCard title="Total Production" value={fmtKm(totalMeters)} subtitle={`${totalTokens} drums`} /></Col>
        <Col xs={6} md={3}><KpiCard title="Avg Speed" value={fmtSpeed(overallAvgSpeed)} subtitle="while running" color="info" /></Col>
        <Col xs={6} md={3}><KpiCard title="Running Time" value={fmtMin(totalRunningSec)} subtitle={`${fmtMin(totalIdleSec)} idle`} color="warning" /></Col>
        <Col xs={6} md={3}><KpiCard title="Operators" value={operators.length} color="success" /></Col>
      </Row>
      <Row className="g-3">
        <Col md={6}>
          <ChartCard title="Total Meters Produced" height={`${chartH}px`}>
            <ReactEChartsCore echarts={echarts} option={metersOption} style={{ height: `${chartH}px`, width: '100%' }} notMerge lazyUpdate />
          </ChartCard>
        </Col>
        <Col md={6}>
          <ChartCard title="Average Speed (m/min)" height={`${chartH}px`}>
            <ReactEChartsCore echarts={echarts} option={speedOption} style={{ height: `${chartH}px`, width: '100%' }} notMerge lazyUpdate />
          </ChartCard>
        </Col>
      </Row>
      <Row className="g-3 mt-1">
        <Col xs={12}>
          <ChartCard title="Time at the machine — running vs idle" height={`${chartH}px`}>
            <ReactEChartsCore echarts={echarts} option={timeOption} style={{ height: `${chartH}px`, width: '100%' }} notMerge lazyUpdate />
          </ChartCard>
        </Col>
      </Row>
    </>
  );
};

// ════════════════════════════════════════════════════════════════════
// TAB 2: COMPARISON
// ════════════════════════════════════════════════════════════════════

// Head-to-head: one bar per operator inside each product/phase they share.
// This is the view that answers "who is faster at this", which a single ranking
// of overall speed cannot — the groups are different jobs.
const PeerComparisonChart = ({ rows }) => {
  const { getThemeColor } = useAppContext();

  const { groups, operators, series } = useMemo(() => {
    const byGroup = new Map();
    for (const r of rows) {
      if (r.speed == null) continue;
      const label = `${r.productLabel} · ${r.phaseLabel || r.phase}${r.tandem ? ' [tandem]' : ''}`;
      if (!byGroup.has(label)) byGroup.set(label, new Map());
      byGroup.get(label).set(r.operator, r.speed);
    }
    // only groups where at least two people can actually be put side by side
    const shared = [...byGroup.entries()].filter(([, m]) => m.size > 1);
    const list = shared.length ? shared : [...byGroup.entries()];
    const ops = [...new Set(list.flatMap(([, m]) => [...m.keys()]))].sort();
    return {
      groups: list.map(([label]) => label),
      operators: ops,
      series: ops.map((op, i) => ({
        name: op,
        type: 'bar',
        barMaxWidth: 22,
        itemStyle: { color: COLORS[i % COLORS.length], borderRadius: [3, 3, 0, 0] },
        data: list.map(([, m]) => m.get(op) ?? null)
      }))
    };
  }, [rows]);

  if (!groups.length) return null;

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      valueFormatter: v => (v == null ? '—' : `${v} m/min`)
    },
    legend: { top: 0, textStyle: { color: getThemeColor('gray-700'), fontSize: 11 } },
    grid: { left: 10, right: 20, top: 40, bottom: 10, containLabel: true },
    xAxis: {
      type: 'category',
      data: groups,
      axisLabel: {
        color: getThemeColor('gray-800'), fontSize: 10, interval: 0,
        rotate: groups.length > 3 ? 18 : 0
      },
      axisTick: false,
      axisLine: { lineStyle: { color: getThemeColor('gray-300') } }
    },
    yAxis: {
      type: 'value', name: 'm/min',
      nameTextStyle: { color: getThemeColor('gray-600') },
      axisLabel: { color: getThemeColor('gray-600'), fontSize: 11 },
      splitLine: { lineStyle: { color: getThemeColor('gray-200') } },
      axisLine: { show: false }
    },
    series
  };

  const h = Math.max(320, groups.length * 46 + 120);
  return (
    <ChartCard
      title={`Head to head — ${operators.length} ${operators.length === 1 ? 'operator' : 'operators'} on the same work`}
      height={`${h}px`}
    >
      <ReactEChartsCore
        echarts={echarts}
        option={option}
        style={{ height: `${h}px`, width: '100%' }}
        notMerge
        lazyUpdate
      />
    </ChartCard>
  );
};

const ComparisonTab = ({ filters }) => {
  const { data, isLoading } = useOperatorMatrix(filters);
  const rows = data?.data || [];
  const groups = data?.groups || [];

  const [bucket, setBucket] = useState('week');
  const trendFilters = useMemo(() => ({ ...filters, bucket }), [filters, bucket]);
  const { data: trend, isLoading: trendLoading } = useOperatorTrend(trendFilters);

  // the cell whose drums are being inspected
  const [drill, setDrill] = useState(null);

  const comparable = rows.filter(r => r.comparable);
  const bestPerGroup = groups.filter(g => g.operators > 1).length;

  return (
    <>
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <KpiCard title="Operator / Product / Phase" value={rows.length} subtitle="rows measured" />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard
            title="Directly Comparable"
            value={comparable.length}
            subtitle={`${bestPerGroup} jobs run by more than one`}
            color="info"
          />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard
            title="Above Their Peers"
            value={comparable.filter(r => (r.deltaPct ?? 0) > 3).length}
            color="success"
          />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard
            title="Below Their Peers"
            value={comparable.filter(r => (r.deltaPct ?? 0) < -3).length}
            color="warning"
          />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col xs={12}>
          <PeerComparisonChart rows={rows} />
        </Col>
        <Col xs={12}>
          <OperatorTrendChart
            rows={trend?.data || []}
            buckets={trend?.buckets || []}
            isLoading={trendLoading}
            bucket={bucket}
            onBucketChange={setBucket}
          />
        </Col>
      </Row>

      <Card>
        <Card.Header className="py-2">
          <h6 className="mb-0">Speed by operator, product and phase</h6>
          <p className="mb-0 fs-10 text-700">
            Each row is compared only against operators who ran the same product, in
            the same phase, on the same machine — an overall speed ranking would just
            reflect who happened to run thin wire on a fast line.
          </p>
        </Card.Header>
        <Card.Body className="p-0">
          <OperatorMatrixTable rows={rows} isLoading={isLoading} onRowClick={setDrill} />
        </Card.Body>
      </Card>

      <DrillTokensModal cell={drill} show={!!drill} onHide={() => setDrill(null)} />
    </>
  );
};

// ════════════════════════════════════════════════════════════════════
// TAB 4: PRODUCTS
// ════════════════════════════════════════════════════════════════════
const ProductsTab = ({ filters }) => {
  const { getThemeColor } = useAppContext();
  const { data, isLoading } = useOperatorMatrix(filters);
  const groups = data?.groups || [];
  const rows = data?.data || [];

  if (isLoading) return <LoadingState />;
  if (!groups.length) return <EmptyState message="No production recorded for these filters" />;

  const totalMeters = groups.reduce((s, g) => s + (g.meters || 0), 0);
  const totalDrums = groups.reduce((s, g) => s + (g.tokens || 0), 0);
  const tandemGroups = groups.filter(g => g.tandem).length;
  const multiOperator = groups.filter(g => g.operators > 1).length;

  const top = groups.slice(0, 14);
  const chartH = Math.max(280, top.length * 32 + 60);

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      formatter: params => {
        const g = top[params[0]?.dataIndex];
        if (!g) return '';
        const mach = g.machineId ? ' - ' + g.machineId.toUpperCase() : '';
        const spd = g.speed != null ? g.speed + ' m/min' : 'no speed';
        return '<b>' + g.productLabel + '</b><br/>' + g.phaseLabel + mach +
          '<br/>' + fmtKm(g.meters) + ' in ' + g.tokens + ' drums' +
          '<br/>Speed: <b>' + spd + '</b>' +
          '<br/>' + g.operators + (g.operators === 1 ? ' operator' : ' operators');
      }
    },
    grid: { left: 230, right: 45, top: 10, bottom: 10, containLabel: false },
    xAxis: {
      type: 'value',
      axisLabel: { color: getThemeColor('gray-600'), fontSize: 11 },
      splitLine: { lineStyle: { color: getThemeColor('gray-200') } }
    },
    yAxis: {
      type: 'category',
      data: top.map(g => g.productLabel + ' / ' + g.phaseLabel + (g.tandem ? ' [tandem]' : '')),
      axisLabel: { color: getThemeColor('gray-800'), fontSize: 10 },
      axisTick: false,
      axisLine: { lineStyle: { color: getThemeColor('gray-300') } }
    },
    series: [{
      type: 'bar',
      barWidth: 16,
      data: top.map((g, i) => ({
        value: Math.round(g.meters),
        itemStyle: { color: COLORS[i % COLORS.length], borderRadius: [0, 3, 3, 0] }
      })),
      label: {
        show: true, position: 'right', fontSize: 10,
        color: getThemeColor('gray-700'),
        formatter: p => fmtKm(p.value)
      }
    }]
  };

  return (
    <>
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <KpiCard title="Total Produced" value={fmtKm(totalMeters)} subtitle={`${totalDrums} drums`} />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard title="Product / Phase Combos" value={groups.length} color="info" />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard title="Tandem Runs" value={tandemGroups} subtitle="two layers in one pass" color="warning" />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard title="Run by 2+ Operators" value={multiOperator} subtitle="where comparison is possible" color="success" />
        </Col>
      </Row>

      <Row className="g-3">
        <Col xs={12}>
          <ChartCard title="Production by product and phase" height={`${chartH}px`}>
            <ReactEChartsCore echarts={echarts} option={option} style={{ height: `${chartH}px`, width: '100%' }} notMerge lazyUpdate />
          </ChartCard>
        </Col>

        <Col xs={12}>
          <Card>
            <Card.Header className="py-2">
              <h6 className="mb-0">Every operator who ran each product and phase</h6>
              <p className="mb-0 fs-10 text-700">
                A tandem run extrudes two layers in one pass, so it is listed as its
                own operation rather than mixed in with a plain sheath.
              </p>
            </Card.Header>
            <Card.Body className="p-0">
              <OperatorMatrixTable rows={rows} isLoading={isLoading} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

// ════════════════════════════════════════════════════════════════════
// TAB 5: MATERIAL
// ════════════════════════════════════════════════════════════════════
const MATERIAL_COLOR = {
  COPPER: '#e5780b', PVC: '#3874ff', POLIETILEN: '#10b981',
  PE: '#10b981', BLR: '#64748b', STEEL: '#64748b', ALUMINIUM: '#a855f7'
};

const MaterialTab = ({ filters }) => {
  const { getThemeColor } = useAppContext();
  const { data, isLoading } = useMaterialMatrix(filters);
  const rows = data?.data || [];
  const groups = data?.groups || [];

  if (isLoading) return <LoadingState />;
  if (!rows.length && !groups.length) {
    return <EmptyState message="No material consumption recorded for these filters" />;
  }

  const totalKg = groups.reduce((s, g) => s + (g.kg || 0), 0);
  const byMaterial = new Map();
  for (const g of groups) byMaterial.set(g.material, (byMaterial.get(g.material) || 0) + (g.kg || 0));
  const materials = [...byMaterial.entries()].sort((a, b) => b[1] - a[1]);
  const comparable = rows.filter(r => r.comparable);

  const spread = groups.filter(g => g.kgPerKm != null).slice(0, 12);
  const chartH = Math.max(260, spread.length * 30 + 60);

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      formatter: params => {
        const g = spread[params[0]?.dataIndex];
        if (!g) return '';
        const grade = g.grade ? ' (' + g.grade + ')' : '';
        return '<b>' + g.productLabel + '</b> / ' + g.phaseLabel + '<br/>' +
          g.material + grade + '<br/><b>' + g.kgPerKm + ' kg/km</b> - ' +
          g.kg.toFixed(0) + ' kg over ' + fmtKm(g.meters) + '<br/>' +
          g.operators + (g.operators === 1 ? ' operator' : ' operators');
      }
    },
    grid: { left: 230, right: 55, top: 10, bottom: 10, containLabel: false },
    xAxis: {
      type: 'value', name: 'kg/km',
      nameTextStyle: { color: getThemeColor('gray-600') },
      axisLabel: { color: getThemeColor('gray-600'), fontSize: 11 },
      splitLine: { lineStyle: { color: getThemeColor('gray-200') } }
    },
    yAxis: {
      type: 'category',
      data: spread.map(g => g.productLabel + ' / ' + g.material),
      axisLabel: { color: getThemeColor('gray-800'), fontSize: 10 },
      axisTick: false,
      axisLine: { lineStyle: { color: getThemeColor('gray-300') } }
    },
    series: [{
      type: 'bar', barWidth: 16,
      data: spread.map(g => ({
        value: g.kgPerKm,
        itemStyle: {
          color: MATERIAL_COLOR[g.material] || getThemeColor('secondary'),
          borderRadius: [0, 3, 3, 0]
        }
      })),
      label: {
        show: true, position: 'right', fontSize: 10,
        color: getThemeColor('gray-700'),
        formatter: p => `${p.value}`
      }
    }]
  };

  return (
    <>
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}><KpiCard title="Total Consumed" value={fmtKg(totalKg)} /></Col>
        {materials.slice(0, 2).map(([mat, kg]) => (
          <Col xs={6} md={3} key={mat}>
            <KpiCard
              title={mat}
              value={fmtKg(kg)}
              subtitle={`${Math.round((kg / (totalKg || 1)) * 100)}% of total`}
              color="info"
            />
          </Col>
        ))}
        <Col xs={6} md={3}>
          <KpiCard title="Comparable Rows" value={comparable.length} subtitle="same product, 2+ operators" color="success" />
        </Col>
      </Row>

      <Row className="g-3">
        <Col xs={12}>
          <ChartCard title="Material intensity by product and phase (kg per km)" height={`${chartH}px`}>
            <ReactEChartsCore echarts={echarts} option={option} style={{ height: `${chartH}px`, width: '100%' }} notMerge lazyUpdate />
          </ChartCard>
        </Col>

        <Col xs={12}>
          <Card>
            <Card.Header className="py-2">
              <h6 className="mb-0">Material per operator - kg per kilometre</h6>
              <p className="mb-0 fs-10 text-700">
                Total kilograms only say who produced more. With the geometry fixed
                by the standard, the variation in kg/km is wall thickness - running
                rich or lean against the same spec - and that is the part an
                operator controls.
              </p>
            </Card.Header>
            <Card.Body className="p-0">
              <MaterialMatrixTable rows={rows} isLoading={isLoading} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

// ════════════════════════════════════════════════════════════════════
// TAB: DOWNTIME — measured from STATUS, not inferred from token transitions
// ════════════════════════════════════════════════════════════════════
const REASON_LABEL = {
  COLOR_CHANGE: 'Color Change',
  COLOR_CHANGE_ON_FLY: 'Color Change (on the fly)',
  PRODUCT_CHANGEOVER: 'Product Changeover',
  PHASE_CHANGEOVER: 'Phase Changeover',
  DRUM_CHANGE: 'Drum Change',
  CLEAN_UP: 'Clean Up',
  UNWINDING: 'Unwinding',
  DOWNTIME: 'Breakdown',
  IN_DRUM_STOP: 'In-Drum Stop',
  IDLE: 'Idle',
  COLD_START: 'Cold Start'
};

const REASON_COLOR = {
  COLOR_CHANGE: '#f59e0b',
  COLOR_CHANGE_ON_FLY: '#fcd34d',
  PRODUCT_CHANGEOVER: '#ef4444',
  PHASE_CHANGEOVER: '#6366f1',
  DRUM_CHANGE: '#10b981',
  CLEAN_UP: '#06b6d4',
  UNWINDING: '#a855f7',
  DOWNTIME: '#dc2626',
  IN_DRUM_STOP: '#94a3b8',
  IDLE: '#64748b',
  COLD_START: '#0ea5e9'
};

const reasonLabel = r => REASON_LABEL[r] || r;
// Below this many observations a number is noise — the forecast falls back to
// the machine median, so the UI must not present it as this person's figure.
const LOW_SAMPLES = 3;


// Head-to-head on downtime: one bar per operator inside each transition type
// they both hit. Less is better here, which the axis label spells out so the
// chart is not read the same way as the speed one.
const DowntimeComparisonChart = ({ rows }) => {
  const { getThemeColor } = useAppContext();

  const { groups, operators, series } = useMemo(() => {
    const byGroup = new Map();
    for (const r of rows) {
      if (!r.operator || r.medianCostSec == null) continue;
      const label = `${REASON_LABEL[r.reason] || r.reason}${
        r.productLabel ? ` · ${r.productLabel}` : ''
      }`;
      if (!byGroup.has(label)) byGroup.set(label, new Map());
      byGroup.get(label).set(r.operator, Number((r.medianCostSec / 60).toFixed(1)));
    }
    const shared = [...byGroup.entries()].filter(([, m]) => m.size > 1);
    const list = shared.length ? shared : [...byGroup.entries()];
    const ops = [...new Set(list.flatMap(([, m]) => [...m.keys()]))].sort();
    return {
      groups: list.map(([label]) => label),
      operators: ops,
      series: ops.map((op, i) => ({
        name: op,
        type: 'bar',
        barMaxWidth: 22,
        itemStyle: { color: COLORS[i % COLORS.length], borderRadius: [3, 3, 0, 0] },
        data: list.map(([, m]) => m.get(op) ?? null)
      }))
    };
  }, [rows]);

  if (!groups.length) return null;

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      valueFormatter: v => (v == null ? '—' : `${v} min`)
    },
    legend: { top: 0, textStyle: { color: getThemeColor('gray-700'), fontSize: 11 } },
    grid: { left: 10, right: 20, top: 40, bottom: 10, containLabel: true },
    xAxis: {
      type: 'category',
      data: groups,
      axisLabel: {
        color: getThemeColor('gray-800'), fontSize: 10, interval: 0,
        rotate: groups.length > 3 ? 18 : 0
      },
      axisTick: false,
      axisLine: { lineStyle: { color: getThemeColor('gray-300') } }
    },
    yAxis: {
      type: 'value',
      name: 'min (lower is better)',
      nameTextStyle: { color: getThemeColor('gray-600') },
      axisLabel: { color: getThemeColor('gray-600'), fontSize: 11 },
      splitLine: { lineStyle: { color: getThemeColor('gray-200') } },
      axisLine: { show: false }
    },
    series
  };

  const h = Math.max(320, groups.length * 46 + 120);
  return (
    <ChartCard
      title={`Head to head — ${operators.length} ${operators.length === 1 ? 'operator' : 'operators'} on the same stoppages`}
      height={`${h}px`}
    >
      <ReactEChartsCore
        echarts={echarts}
        option={option}
        style={{ height: `${h}px`, width: '100%' }}
        notMerge
        lazyUpdate
      />
    </ChartCard>
  );
};

const DowntimeTab = ({ filters }) => {
  const { getThemeColor } = useAppContext();
  const { data, isLoading } = useIntervalStats(filters);
  const { data: matrixData, isLoading: matrixLoading } = useDowntimeMatrix(filters);
  const matrixRows = matrixData?.data || [];

  const totals = data?.totals || null;
  const perMachine = data?.perMachine || [];
  const perOperator = data?.perOperator || [];

  if (isLoading) return <LoadingState />;
  if (!totals || !totals.intervals) {
    return (
      <EmptyState message="No intervals recorded yet. They appear as machines stop and start again." />
    );
  }

  const machines = [...new Set(perMachine.map(r => r.machineId))].sort();
  const reasons = [...new Set(perMachine.map(r => r.reason))];

  // One series per machine so the same transition can be compared across lines:
  // a colour change is not the same job on L75 as on L120.
  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      formatter: params => {
        const rows = params
          .filter(p => p.value > 0)
          .map(p => {
            const hit = perMachine.find(
              x => x.machineId === p.seriesName && reasonLabel(x.reason) === p.name
            );
            return `${p.marker} ${p.seriesName}: <b>${p.value} min</b> <span style="opacity:.7">(${hit?.samples ?? 0} samples)</span>`;
          })
          .join('<br/>');
        return `<b>${params[0]?.name}</b><br/>${rows || 'no data'}`;
      }
    },
    legend: { top: 0, textStyle: { color: getThemeColor('gray-700'), fontSize: 11 } },
    grid: { left: 10, right: 20, top: 40, bottom: 10, containLabel: true },
    xAxis: {
      type: 'category',
      data: reasons.map(reasonLabel),
      axisLabel: {
        color: getThemeColor('gray-800'), fontSize: 11, interval: 0,
        rotate: reasons.length > 4 ? 20 : 0
      },
      axisTick: false,
      axisLine: { lineStyle: { color: getThemeColor('gray-300') } }
    },
    yAxis: {
      type: 'value',
      name: 'min (median)',
      nameTextStyle: { color: getThemeColor('gray-600') },
      axisLabel: { color: getThemeColor('gray-600'), fontSize: 11 },
      splitLine: { lineStyle: { color: getThemeColor('gray-200') } },
      axisLine: { show: false }
    },
    series: machines.map((mid, i) => ({
      name: mid,
      type: 'bar',
      barMaxWidth: 26,
      itemStyle: { color: COLORS[i % COLORS.length], borderRadius: [3, 3, 0, 0] },
      data: reasons.map(r => {
        const hit = perMachine.find(x => x.machineId === mid && x.reason === r);
        return hit?.medianSec != null ? Number((hit.medianSec / 60).toFixed(1)) : 0;
      })
    }))
  };

  const measuredPct = totals.intervals
    ? Math.round((totals.measured / totals.intervals) * 100)
    : 0;

  return (
    <>
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <KpiCard title="Intervals Recorded" value={totals.intervals} />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard
            title="With Measured Work"
            value={`${measuredPct}%`}
            subtitle={`${totals.unmeasured} never touched`}
            color="info"
          />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard title="Breakdowns" value={totals.downtime} color="danger" />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard title="Transition Types" value={reasons.length} color="success" />
        </Col>
      </Row>

      <Row className="g-3">
        <Col xs={12}>
          <ChartCard title="Median Transition Time by Machine" height="380px">
            <ReactEChartsCore
              echarts={echarts}
              option={option}
              style={{ height: '380px', width: '100%' }}
              notMerge
              lazyUpdate
            />
          </ChartCard>
        </Col>

        <Col xs={12}>
          <DowntimeComparisonChart rows={matrixRows} />
        </Col>

        <Col xs={12}>
          <Card>
            <Card.Header className="py-2">
              <h6 className="mb-0">Downtime by operator, product, phase and reason</h6>
              <p className="mb-0 fs-10 text-700">
                Each row is compared against the other operators who hit the same
                situation — losing 20 minutes to a changeover only means something
                next to what everyone else loses on that product.
              </p>
            </Card.Header>
            <Card.Body className="p-0">
              <DowntimeMatrixTable rows={matrixRows} isLoading={matrixLoading} />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12}>
          <Card>
            <Card.Header className="py-2">
              <h6 className="mb-0">Median per operator and transition</h6>
            </Card.Header>
            <Card.Body className="p-0">
              {perOperator.length ? (
                <div className="table-responsive">
                  <table className="table table-sm fs-9 mb-0">
                    <thead>
                      <tr>
                        <th className="text-800">Operator</th>
                        <th className="text-800">Machine</th>
                        <th className="text-800">Transition</th>
                        <th className="text-800 text-end">Median</th>
                        <th className="text-800 text-end">Samples</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perOperator.map((r, i) => (
                        <tr key={`${r.machineId}-${r.operator}-${r.reason}-${i}`}>
                          <td className="fw-semibold">{r.operator}</td>
                          <td className="text-uppercase text-700">{r.machineId}</td>
                          <td>
                            <span
                              className="badge rounded-pill"
                              style={{
                                backgroundColor: REASON_COLOR[r.reason] || '#6b7280',
                                fontSize: '0.7rem'
                              }}
                            >
                              {reasonLabel(r.reason)}
                            </span>
                          </td>
                          <td className="text-end fw-semibold">{fmtMin(r.medianSec)}</td>
                          <td className="text-end">
                            {r.samples < LOW_SAMPLES ? (
                              <span
                                className="text-warning"
                                title="Too few samples — the forecast falls back to the machine median, not this operator's own figure"
                              >
                                {r.samples} ⚠
                              </span>
                            ) : (
                              r.samples
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5 text-700 fs-9">
                  No intervals attributed to an operator yet.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

// ════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════════════
const TABS = [
  ['overview', 'Overview'],
  ['comparison', 'Comparison'],
  ['downtime', 'Downtime'],
  ['products', 'Products'],
  ['material', 'Material']
];

const OperatorPerformance = () => {
  const [tab, setTab] = useState('overview');
  const [machineId, setMachineId] = useState('');
  const [phase, setPhase] = useState('');
  const [productLabel, setProductLabel] = useState('');
  const [operators, setOperators] = useState([]);
  // Opens on the current month. It used to open on nothing, which left each tab
  // to fall back to whatever window its own endpoint happened to pick — 30 days
  // on one, 90 on another — so two panels on the same screen could be answering
  // about different months. It also keeps the scan bounded as history grows.
  const [dateRange, setDateRange] = useState(thisMonth);

  const period = useMemo(() => {
    if (dateRange?.length !== 2) return {};
    return {
      dateFrom: dayjs(dateRange[0]).startOf('day').valueOf(),
      dateTo: dayjs(dateRange[1]).endOf('day').valueOf()
    };
  }, [dateRange]);

  // What each tab actually honours. A filter the active tab ignores is dropped
  // from its query rather than sent and silently discarded, so the request and
  // the bar always agree about what is being asked.
  const applies = TAB_FILTERS[tab] || TAB_FILTERS.overview;
  const filters = useMemo(() => {
    const f = { ...period };
    if (machineId && applies.machine === true) f.machineId = machineId;
    if (phase && applies.phase === true) f.phase = phase;
    if (productLabel && applies.product === true) f.productLabel = productLabel;
    if (operators.length && applies.operators === true) f.operators = operators;
    return f;
  }, [period, machineId, phase, productLabel, operators, applies]);

  // The strip: everyone who produced in this period, narrowed by machine/phase/
  // product but NOT by the operator selection — the whole roster has to stay on
  // screen, or you could never add a second person to a comparison.
  const stripFilters = useMemo(() => {
    const f = { ...period };
    if (machineId) f.machineId = machineId;
    if (phase) f.phase = phase;
    if (productLabel) f.productLabel = productLabel;
    return f;
  }, [period, machineId, phase, productLabel]);

  const { data: summary, isLoading: summaryLoading } = useOperatorSummary(stripFilters);
  const strip = useMemo(() => summary?.data || [], [summary]);

  // Product options come from the period alone, so narrowing by machine cannot
  // empty the list you would use to widen it again.
  const { data: allMatrix } = useOperatorMatrix(period);
  const productOptions = useMemo(
    () => [...new Set((allMatrix?.data || []).map(r => r.productLabel).filter(Boolean))].sort(),
    [allMatrix]
  );

  const scope = useMemo(() => {
    if (!strip.length) return null;
    const drums = strip.reduce((s, o) => s + (o.totalTokens || 0), 0);
    const metres = strip.reduce((s, o) => s + (o.totalProducedLength || 0), 0);
    const machines = new Set(strip.flatMap(o => o.machines || [])).size;
    const days =
      dateRange?.length === 2 ? dayjs(dateRange[1]).diff(dayjs(dateRange[0]), 'day') + 1 : null;
    return [
      `${drums} drum${drums === 1 ? '' : 's'}`,
      fmtKm(metres),
      `${strip.length} operator${strip.length === 1 ? '' : 's'}`,
      `${machines} machine${machines === 1 ? '' : 's'}`,
      days ? `${days} day${days === 1 ? '' : 's'}` : null
    ]
      .filter(Boolean)
      .join(' · ');
  }, [strip, dateRange]);

  return (
    <div className="mb-9 mt-0">
      <div className="mb-3">
        <h2 className="mb-1">Operator Performance</h2>
        <p className="text-700 mb-0">
          Speed, downtime and material — each operator measured against the others who ran the
          same product, in the same phase
        </p>
      </div>

      <OperatorScopeBar
        tab={tab}
        machineId={machineId}
        setMachineId={setMachineId}
        phase={phase}
        setPhase={setPhase}
        productLabel={productLabel}
        setProductLabel={setProductLabel}
        operators={operators}
        setOperators={setOperators}
        dateRange={dateRange}
        setDateRange={setDateRange}
        productOptions={productOptions}
        scope={scope}
      />

      <OperatorStrip
        rows={strip}
        selected={operators}
        onChange={setOperators}
        isLoading={summaryLoading}
      />

      <Tab.Container activeKey={tab} onSelect={k => k && setTab(k)}>
        <Nav variant="tabs" className="mb-3">
          {TABS.map(([key, label]) => (
            <Nav.Item key={key}>
              <Nav.Link eventKey={key}>{label}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        {/* Keyed on the filters so a tab remounts when the scope changes rather
            than showing the previous period's numbers until its query settles. */}
        <Tab.Content>
          <Tab.Pane eventKey="overview" mountOnEnter unmountOnExit>
            <OverviewTab filters={filters} />
          </Tab.Pane>
          <Tab.Pane eventKey="comparison" mountOnEnter unmountOnExit>
            <ComparisonTab filters={filters} />
          </Tab.Pane>
          <Tab.Pane eventKey="downtime" mountOnEnter unmountOnExit>
            <DowntimeTab filters={filters} />
          </Tab.Pane>
          <Tab.Pane eventKey="products" mountOnEnter unmountOnExit>
            <ProductsTab filters={filters} />
          </Tab.Pane>
          <Tab.Pane eventKey="material" mountOnEnter unmountOnExit>
            <MaterialTab filters={filters} />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default OperatorPerformance;
